import { useState, useCallback, useEffect, useMemo } from "react";
import type { LiveFinding } from "@/hooks/use-walkthrough-stream";
import { designerReviewFromReport } from "@iamu/shared";
import type {
  LiveWalkthroughData,
  UnifiedLiveFinding,
  LiveRightTab,
  Severity,
} from "./types";
import type { LiveStepDisplay } from "./live-walkthrough-screenshot";
import {
  buildUnifiedFindings,
  buildSeverityCounts,
  buildCitationsMap,
} from "./findings-aggregator";
import { useLiveScreenshot } from "./use-live-screenshot";
import { usePaneResizer } from "./use-pane-resizer";

interface UseLiveWalkthroughStateProps {
  walkthrough: LiveWalkthroughData;
  streamSteps: Array<{
    stepNumber: number;
    title: string;
    personaReasoning: string;
    grounding: string | null;
    screenshotPath: string | null;
  }>;
  streamLiveFindings: LiveFinding[];
  isRunning: boolean;
  isComplete: boolean;
  isErrored: boolean;
}

export function useLiveWalkthroughState({
  walkthrough,
  streamSteps,
  streamLiveFindings,
  isRunning,
  isComplete,
  isErrored,
}: UseLiveWalkthroughStateProps) {
  const [activeStepIdx, setActiveStepIdx] = useState(0);
  const [isLive, setIsLive] = useState(true);
  const [activeTab, setActiveTab] = useState<LiveRightTab>("timeline");
  const [activeSeverityFilter, setActiveSeverityFilter] = useState<
    Severity | "all" | "helps"
  >("all");
  const [mobileTab, setMobileTab] = useState<
    "screenshot" | "timeline" | "findings" | "designer"
  >("screenshot");
  const [panelCitation, setPanelCitation] = useState<{
    sourceType: string;
    sourceId: string;
  } | null>(null);

  const { dividerX, containerRef, handleDividerMouseDown } = usePaneResizer();

  const displaySteps: LiveStepDisplay[] = useMemo(() => {
    if (streamSteps.length > 0) {
      return streamSteps.map((s) => ({
        id: `stream-${s.stepNumber}`,
        stepNumber: s.stepNumber,
        title: s.title,
        personaReasoning: s.personaReasoning,
        grounding: s.grounding,
        screenshotPath: s.screenshotPath,
      }));
    }
    return walkthrough.steps.map((s) => ({ ...s }));
  }, [streamSteps, walkthrough.steps]);

  useEffect(() => {
    if (streamSteps.length > 0 && isLive) {
      setActiveStepIdx(streamSteps.length - 1);
    }
  }, [streamSteps.length, isLive]);

  const latestStepIdx = Math.max(0, displaySteps.length - 1);
  const stepsBehindLive = Math.max(0, latestStepIdx - activeStepIdx);
  const displayedStep = displaySteps[activeStepIdx];

  const { screenshotBlobUrl, screenshotLoading } = useLiveScreenshot(
    walkthrough.id,
    displayedStep,
  );

  const stepNumberByStepId = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of walkthrough.steps) map.set(s.id, s.stepNumber);
    return map;
  }, [walkthrough.steps]);

  const unifiedFindings = useMemo(
    () =>
      buildUnifiedFindings(
        walkthrough.findings,
        walkthrough.liveState?.findings,
        streamLiveFindings,
        stepNumberByStepId,
        streamSteps,
      ),
    [
      walkthrough.findings,
      walkthrough.liveState?.findings,
      streamLiveFindings,
      stepNumberByStepId,
      streamSteps,
    ],
  );

  const findingsByStep = useMemo(() => {
    const map = new Map<number, UnifiedLiveFinding[]>();
    for (const f of unifiedFindings) {
      if (f.stepNumber == null) continue;
      const arr = map.get(f.stepNumber) ?? [];
      arr.push(f);
      map.set(f.stepNumber, arr);
    }
    return map;
  }, [unifiedFindings]);

  const designerFindings = useMemo(
    () => unifiedFindings.filter((f) => f.source === "designer"),
    [unifiedFindings],
  );

  const effectiveDesignerReview = useMemo(
    () =>
      walkthrough.designerReview ??
      designerReviewFromReport(walkthrough.reportMarkdown),
    [walkthrough.designerReview, walkthrough.reportMarkdown],
  );

  const { severityCounts, helpsCount } = useMemo(
    () => buildSeverityCounts(unifiedFindings),
    [unifiedFindings],
  );

  const citationsMap = useMemo(
    () => buildCitationsMap(walkthrough.findings),
    [walkthrough.findings],
  );

  const openPanel = useCallback((sourceType: string, sourceId: string) => {
    setPanelCitation({ sourceType, sourceId });
  }, []);

  const handleStepChange = useCallback(
    (idx: number) => {
      const clamped = Math.max(0, Math.min(displaySteps.length - 1, idx));
      setActiveStepIdx(clamped);
      const isAtLatest = clamped === displaySteps.length - 1;
      if (isAtLatest && isRunning && !isComplete && !isErrored) {
        setIsLive(true);
      } else {
        setIsLive(false);
      }
    },
    [displaySteps.length, isRunning, isComplete, isErrored],
  );

  const handleResumeLive = useCallback(() => {
    if (displaySteps.length > 0) {
      setActiveStepIdx(displaySteps.length - 1);
    }
    setIsLive(true);
  }, [displaySteps.length]);

  const handleJumpToStep = useCallback(
    (stepNumber: number) => {
      const idx = displaySteps.findIndex((s) => s.stepNumber === stepNumber);
      if (idx !== -1) {
        setActiveStepIdx(idx);
        setMobileTab("screenshot");
        const isAtLatest = idx === displaySteps.length - 1;
        if (isAtLatest && isRunning && !isComplete && !isErrored) {
          setIsLive(true);
        } else {
          setIsLive(false);
        }
      }
    },
    [displaySteps, isRunning, isComplete, isErrored],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleStepChange(activeStepIdx - 1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleStepChange(activeStepIdx + 1);
      } else if (e.key === "l" || e.key === "L") {
        if (isRunning && !isComplete && !isErrored) {
          e.preventDefault();
          handleResumeLive();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    activeStepIdx,
    handleStepChange,
    handleResumeLive,
    isRunning,
    isComplete,
    isErrored,
  ]);

  const handleSelectFindingFilter = useCallback(
    (filter: Severity | "all" | "helps") => {
      setActiveSeverityFilter(filter);
      setActiveTab("findings");
      setMobileTab("findings");
    },
    [],
  );

  const handleExport = useCallback(() => {
    const htmlArtifact = walkthrough.artifacts.find(
      (a) => a.type === "html_report" && a.content,
    );
    if (!htmlArtifact?.content) return;
    const blob = new Blob([htmlArtifact.content], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `walkthrough-${walkthrough.id.slice(0, 8)}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }, [walkthrough.artifacts, walkthrough.id]);

  return {
    activeStepIdx,
    setActiveStepIdx,
    isLive,
    stepsBehindLive,
    handleResumeLive,
    handleStepChange,
    activeTab,
    setActiveTab,
    activeSeverityFilter,
    setActiveSeverityFilter,
    mobileTab,
    setMobileTab,
    dividerX,
    containerRef,
    displaySteps,
    displayedStep,
    screenshotBlobUrl,
    screenshotLoading,
    unifiedFindings,
    findingsByStep,
    designerFindings,
    effectiveDesignerReview,
    severityCounts,
    helpsCount,
    citationsMap,
    panelCitation,
    setPanelCitation,
    openPanel,
    handleJumpToStep,
    handleSelectFindingFilter,
    handleExport,
    handleDividerMouseDown,
  };
}
